import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Button, Grid, Typography } from '@mui/material';
import TextTruncate from 'react-text-truncate';
import { CardLoader, InfoCard } from '../../../../components';
import { infoCardListStyles } from '../../../../components/info-card-list/info-card-list-helper';
import { SINGLE_NAVIGATION_PATHS } from '../../../../constants/single-navigation-paths';
import {
    useSystemMessageDetails,
    useSystemMessageDetailsQueryProps,
    useSystemMessages,
} from '../../../../hooks';
import { toLocalDateTimeString } from '../../../../lib';
import {
    getChipColorAndText,
    SystemMessageData,
} from '../../../system-messages/system-messages-helper';
import { useNavigate } from 'react-router-dom';
import {
    DEFAULT_DAYS_TO_RETURN,
    SYSTEM_MESSAGES_CARD,
    systemMessageCardStyles,
} from './system-messages-card-helper';
import { markdownToTxt } from 'markdown-to-txt';
import utf8 from 'utf8';

export const SystemMessagesCard = (): React.JSX.Element => {
    const navigate = useNavigate();
    const [mostRecentSystemMessage, setMostRecentSystemMessage] = useState<
        SystemMessageData | undefined
    >(undefined);
    const [mostRecentSystemMessageBody, setMostRecentSystemMessageBody] =
        useState<string>('');
    const {
        data: messagesListResponse,
        isFetched: isMessageListFetched,
        isLoading: isMessageListLoading,
    } = useSystemMessages({
        daysToReturn: DEFAULT_DAYS_TO_RETURN,
    });
    const {
        data: systemMessageDataResponse,
        isFetched: isSystemMessageFetched,
        refetch: getMessageDetails,
        isLoading: isSystemMessageDetailsLoading,
    } = useSystemMessageDetails({
        systemMessageId: mostRecentSystemMessage?.messageId,
        queryProps: useSystemMessageDetailsQueryProps,
    });

    useEffect(() => {
        if (
            isMessageListFetched &&
            messagesListResponse &&
            !!messagesListResponse.data.length
        ) {
            const mostRecentMessage: SystemMessageData =
                messagesListResponse.data[0];
            setMostRecentSystemMessage(mostRecentMessage);
        }
    }, [isMessageListFetched, messagesListResponse]);

    useEffect(() => {
        if (isSystemMessageFetched && systemMessageDataResponse?.data) {
            setMostRecentSystemMessageBody(
                atob(systemMessageDataResponse?.data?.contentBase64 ?? '')
            );
        }
    }, [isSystemMessageFetched, systemMessageDataResponse]);

    useEffect(() => {
        if (mostRecentSystemMessage?.messageId) {
            getMessageDetails();
        }
    }, [mostRecentSystemMessage, getMessageDetails]);

    const { icon } = getChipColorAndText(
        mostRecentSystemMessage?.messageType ?? 'ALERT',
        true
    );

    const handleButtonClick = (): void => {
        navigate(SINGLE_NAVIGATION_PATHS.SYSTEM_MESSAGES);
    };

    const displayCardContent =
        isMessageListFetched &&
        messagesListResponse &&
        messagesListResponse.data.length > 0;
    const isDataLoading = isMessageListLoading || isSystemMessageDetailsLoading;
    const displayCardDescription = !displayCardContent;

    const getSystemMessageCardContent = (): React.JSX.Element => {
        if (displayCardContent && !isDataLoading) {
            return (
                <Grid sx={systemMessageCardStyles.cardContainer}>
                    <Grid sx={systemMessageCardStyles.messageTitleContainer}>
                        {icon}
                        <Typography sx={systemMessageCardStyles.cardHeaderText}>
                            {mostRecentSystemMessage?.title ?? ''}
                        </Typography>
                    </Grid>
                    <Grid sx={systemMessageCardStyles.messageBodyContainer}>
                        <TextTruncate
                            line={2}
                            truncateText='...'
                            text={utf8.decode(
                                markdownToTxt(mostRecentSystemMessageBody)
                            )}
                        />
                    </Grid>
                    <Grid sx={systemMessageCardStyles.messageDateContainer}>
                        <Typography sx={systemMessageCardStyles.postedText}>
                            {`Posted: ${toLocalDateTimeString(mostRecentSystemMessage?.createDate, true)}`}
                        </Typography>
                    </Grid>
                </Grid>
            );
        } else if (isDataLoading) {
            return (
                <Grid sx={systemMessageCardStyles.loadingContainer}>
                    <CardLoader />
                </Grid>
            );
        } else {
            return <></>;
        }
    };

    return (
        <InfoCard
            title={SYSTEM_MESSAGES_CARD.TITLE}
            description={
                displayCardDescription ? SYSTEM_MESSAGES_CARD.DESCRIPTION : ''
            }
            action={
                <Button
                    color='inherit'
                    size='medium'
                    style={infoCardListStyles.infoCardButton}
                    onClick={handleButtonClick}
                    startIcon={
                        <Icon
                            icon={'mdi:arrow-right'}
                            style={infoCardListStyles.infoCardIcon}
                        />
                    }
                >
                    {SYSTEM_MESSAGES_CARD.ACTION}
                </Button>
            }
        >
            {getSystemMessageCardContent()}
        </InfoCard>
    );
};
